import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from 'shared/layouts/containers/Main'
import { Button, Form, Row } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import InputWrapper from 'shared/components/InputWrapper'
import resAddServiceStatus from './resolvers/resAddServiceStatus'
import useInit from './hooks/useInit'
import useFocusError from 'shared/hooks/useFocusError'
import prepareSubmitForm from 'shared/utils/prepareSubmitForm'
import submitForm from './utils/submitForm'
import getBrandContent from 'utils/getBrandContent'
import Footer from 'shared/components/Footer'
import BackButton from 'shared/components/BackButton'
import SelectWrapper from 'shared/components/SelectWrapper'

const AddServiceStatus: React.FunctionComponent = () => {
  const [showOverlay, setShowOverlay] = useState<boolean>(true)
  const [dataLoaded, setDataLoaded] = useState<boolean>(false)
  const [serviceStatuses, setServiceStatuses] = useState<any[]>([])

  const submitRef = React.createRef<HTMLButtonElement>()

  const { id } = useParams()

  const navigate = useNavigate()

  const {
    register,
    control,
    getValues,
    handleSubmit,
    reset,
    setFocus,
    setError,
    formState: { errors },
  } = useForm({
    mode: 'all',
    resolver: resAddServiceStatus,
  })

  const filteredServiceStatuses = useMemo(
    () => serviceStatuses.filter((s) => String(s.id) !== String(id)),
    [serviceStatuses, id],
  )

  useInit({
    getValues,
    id,
    reset,
    setDataLoaded,
    setShowOverlay,
    setServiceStatuses,
  })

  useFocusError({
    errors,
    setFocus,
  })

  return (
    <Layout
      footerContent={<Footer />}
      brandContent={getBrandContent()}
      showOverlay={showOverlay}
      showCardHeader={true}
      showMinimizedBarButton={true}
      headerInsert={<BackButton url='/service-statuses' text='All Service Statuses' />}
      actionInsert={
        <Button
          disabled={!dataLoaded}
          type='submit'
          name='save'
          className='btn-round btn-primary'
          onClick={() => {
            if (submitRef.current != null) {
              submitRef.current.click()
            }
          }}
        >
          Save Service Status
        </Button>
      }
    >
      <Form
        onSubmit={handleSubmit((data) =>
          prepareSubmitForm({
            submitForm,
            navigate,
            data,
            setShowOverlay,
            id,
            name: 'Service Status',
            successNav: '/service-statuses',
            setError,
          }),
        )}
        id='main-form'
      >
        <InputWrapper label='Name' name='name' errors={errors}>
          <Form.Control type='text' {...register('name')} />
        </InputWrapper>

        <InputWrapper label='Show' name='show' errors={errors}>
          <Form.Select {...register('show')}>
            <option key={0} value={1}>
              Yes
            </option>
            <option key={1} value={0}>
              No
            </option>
          </Form.Select>
        </InputWrapper>

        <InputWrapper
          label='Enable RMA # Edit Mode for a Device'
          name='allow_rma_edit'
          errors={errors}
        >
          <Form.Select {...register('allow_rma_edit')}>
            <option key={0} value={1}>
              Yes
            </option>
            <option key={1} value={0}>
              No
            </option>
          </Form.Select>
        </InputWrapper>

        <InputWrapper label='Initiate Device Replacement' name='init_replacement' errors={errors}>
          <Form.Select {...register('init_replacement')}>
            <option key={0} value={0}>
              No
            </option>
            <option key={1} value={1}>
              Yes
            </option>
          </Form.Select>
        </InputWrapper>

        <InputWrapper label='Default on Device Import' name='default' errors={errors}>
          <Form.Select {...register('default')}>
            <option key={0} value={0}>
              No
            </option>
            <option key={1} value={1}>
              Yes
            </option>
          </Form.Select>
        </InputWrapper>

        <InputWrapper
          label='Status when Device Used Today'
          name='zeroDaysSinceServiceStatus'
          errors={errors}
        >
          <SelectWrapper
            name='zeroDaysSinceServiceStatus'
            control={control}
            options={filteredServiceStatuses}
            optionLabel='name'
            optionValue='id'
          />
        </InputWrapper>

        <InputWrapper
          label='Status after 100+ Days of Inactivity'
          name='hundredPlusDaysSinceServiceStatus'
          errors={errors}
        >
          <SelectWrapper
            name='hundredPlusDaysSinceServiceStatus'
            control={control}
            options={filteredServiceStatuses}
            optionLabel='name'
            optionValue='id'
          />
        </InputWrapper>

        <Button type='submit' name='save' ref={submitRef} className='d-none'></Button>
      </Form>
    </Layout>
  )
}

export default AddServiceStatus
